<template>
  <div class="cdx-root">
    <header v-if="showHeader" class="cdx-header">
      <div>
        <template v-if="showTitle">
          <p class="cdx-eyebrow">{{ dashboardEyebrow }}</p>
          <h2 class="cdx-title">{{ campaignName || 'Metrics' }}</h2>
        </template>
      </div>
      <button type="button" class="cdx-refresh" :disabled="loading || !campaignId" @click="loadAll">
        <v-icon icon="mdi-refresh" size="16" />
        Refresh
      </button>
    </header>

    <div class="cdx-toolbar">
      <div class="cdx-tabs" role="tablist" aria-label="Dashboard sections">
        <button
          v-for="tab in TABS"
          :key="tab.value"
          type="button"
          class="cdx-tab"
          :class="{ 'is-active': activeTab === tab.value }"
          role="tab"
          :aria-selected="activeTab === tab.value"
          @click="activeTab = tab.value"
        >
          {{ tab.title }}
        </button>
      </div>

      <div class="cdx-daterange" role="group" aria-label="Date range">
        <div class="cdx-daterange__presets">
          <button
            v-for="preset in DATE_PRESETS"
            :key="preset.value"
            type="button"
            class="cdx-daterange__chip"
            :class="{ 'is-active': datePreset === preset.value }"
            @click="setDatePreset(preset.value)"
          >
            {{ preset.title }}
          </button>
        </div>
        <div v-if="datePreset === 'custom'" class="cdx-daterange__custom">
          <input v-model="customDateFrom" type="date" class="cdx-daterange__input" @change="applyCustomRange" />
          <span class="cdx-daterange__sep">–</span>
          <input v-model="customDateTo" type="date" class="cdx-daterange__input" @change="applyCustomRange" />
        </div>
        <span class="cdx-daterange__label">{{ dateRangeLabel }}</span>
      </div>
    </div>

    <p v-if="errorMessage" class="cdx-empty cdx-empty--error">{{ errorMessage }}</p>
    <p v-else-if="loading && !hasAnyData" class="cdx-empty">Loading metrics…</p>

    <template v-else>
      <!-- ============ OVERVIEW ============ -->
      <div v-show="activeTab === 'overview'" class="cdx-panel">
        <div class="cdx-tile-row">
          <!-- Lead status funnel -->
          <article class="cdx-card">
            <div class="cdx-card-title">Lead status</div>
            <div
              class="cdx-funnel-total tnum cdx-funnel-total--click"
              title="Click to view all leads"
              @click="openLeadsByStatus(null, 'All leads')"
            >{{ totalLeadsInFunnel }}</div>
            <p class="cdx-funnel-total-label">leads in campaign</p>
            <div class="cdx-segbar" role="img" :aria-label="leadStatusSegments.ariaLabel">
              <span
                v-for="seg in leadStatusSegments.visible"
                :key="seg.key"
                :style="{ width: seg.pct + '%', background: catColor(seg.colorIndex) }"
              />
              <span
                v-if="leadStatusSegments.otherCount"
                :style="{ width: leadStatusSegments.otherPct + '%', background: 'var(--cdx-cat-other)' }"
              />
            </div>
            <div class="cdx-legend">
              <div
                v-for="seg in leadStatusSegments.visible" :key="seg.key"
                class="cdx-legend-row cdx-legend-row--click"
                title="Click to view these leads"
                @click="openLeadsByStatus(seg.status, seg.label)"
              >
                <span class="cdx-swatch" :style="{ background: catColor(seg.colorIndex) }" />
                {{ seg.label }}
                <span class="cdx-legend-num tnum">{{ seg.count }}</span>
              </div>
              <div
                v-if="leadStatusSegments.otherCount"
                class="cdx-legend-row cdx-legend-row--other"
                :class="{ 'is-open': showOtherStatuses }"
                @click="showOtherStatuses = !showOtherStatuses"
              >
                <span class="cdx-swatch" style="background: var(--cdx-cat-other)" />
                Other ({{ leadStatusSegments.other.length }})
                <span class="cdx-legend-num tnum">{{ leadStatusSegments.otherCount }}</span>
                <span class="cdx-caret">▶</span>
              </div>
              <div v-if="showOtherStatuses" class="cdx-legend-detail">
                <div
                  v-for="seg in leadStatusSegments.other" :key="seg.key"
                  class="cdx-legend-row cdx-legend-row--click"
                  title="Click to view these leads"
                  @click="openDrilldown({ label: seg.label, status: seg.status }, 'STATUS', 'leads')"
                >
                  <span class="cdx-swatch" style="opacity: 0.55; background: var(--cdx-cat-other)" />
                  {{ seg.label }}
                  <span class="cdx-legend-num tnum">{{ seg.count }}</span>
                </div>
              </div>
            </div>
          </article>

          <!-- Call outcomes -->
          <article class="cdx-card">
            <div class="cdx-card-title">Call outcomes</div>
            <div
              class="cdx-funnel-total tnum cdx-funnel-total--click"
              title="Click to view all calls"
              @click="openDrilldown({ label: 'All calls' }, 'TOTAL', 'calls', 'calls')"
            >{{ totalBucket?.callsPlaced ?? 0 }}</div>
            <div class="cdx-outcome-sub">
              <span>total calls</span>
              <span
                class="tnum cdx-outcome-sub__click"
                title="Click to view the unique leads called"
                @click="openDrilldown({ label: 'Leads called' }, 'TOTAL', 'leads', 'calls')"
              ><strong>{{ metricsTotalRoot?.uniqueLeadsCalled ?? '—' }}</strong> unique leads</span>
              <span v-if="totalBucket?.totalTalkTimeSeconds" class="tnum" title="Total connected call duration">
                <strong>{{ totalBucket?.totalTalkTimeLabel }}</strong> talk time
              </span>
            </div>
            <div class="cdx-segbar" role="img" aria-label="Call outcomes breakdown">
              <span
                v-for="seg in callOutcomeSegments"
                :key="seg.outcome"
                :style="{ width: seg.pct + '%', background: outcomeColor(seg.outcome) }"
              />
            </div>
            <div class="cdx-legend">
              <div
                v-for="seg in callOutcomeSegments" :key="seg.outcome"
                class="cdx-legend-row cdx-legend-row--click"
                title="Click to view these calls"
                @click="openDrilldown({ label: seg.outcome, outcome: seg.outcome }, 'OUTCOME', 'calls')"
              >
                <span class="cdx-swatch" :style="{ background: outcomeColor(seg.outcome) }" />
                {{ seg.outcome }}
                <span class="cdx-legend-num tnum">{{ seg.count }}</span>
              </div>
            </div>
          </article>

          <!-- Call duration -->
          <article v-if="callDurationSegments.length" class="cdx-card">
            <div class="cdx-card-title">Call duration</div>
            <p class="cdx-outcome-sub" style="margin-bottom: 10px">
              <span>connected calls, by length</span>
            </p>
            <div class="cdx-segbar" role="img" aria-label="Call duration breakdown">
              <span
                v-for="seg in callDurationSegments"
                :key="seg.bucket"
                :style="{ width: seg.pct + '%', background: catColor(seg.colorIndex) }"
              />
            </div>
            <div class="cdx-legend">
              <div
                v-for="seg in callDurationSegments" :key="seg.bucket"
                class="cdx-legend-row cdx-legend-row--click"
                title="Click to view these calls"
                @click="openDrilldown({ label: seg.bucket, bucket: seg.bucket }, 'DURATION', 'calls')"
              >
                <span class="cdx-swatch" :style="{ background: catColor(seg.colorIndex) }" />
                {{ seg.bucket }}
                <span class="cdx-legend-num tnum">{{ seg.count }}</span>
              </div>
            </div>
          </article>

          <!-- Avg lead score -->
          <article
            class="cdx-card cdx-card--click"
            title="Click to view all leads"
            @click="openLeadsByStatus(null, 'All leads')"
          >
            <div class="cdx-card-title">Avg. lead score</div>
            <div class="cdx-score-wrap">
              <svg width="64" height="64" viewBox="0 0 64 64" style="flex: none">
                <circle cx="32" cy="32" r="26" fill="none" stroke="var(--cdx-surface-2)" stroke-width="7" />
                <circle
                  cx="32" cy="32" r="26" fill="none" stroke="var(--cdx-accent)" stroke-width="7"
                  stroke-linecap="round" :stroke-dasharray="163.4"
                  :stroke-dashoffset="163.4 - (163.4 * scoreRingPct)"
                  transform="rotate(-90 32 32)"
                />
              </svg>
              <div class="cdx-score-value tnum">{{ totalBucket?.avgLeadScoreLabel ?? '—' }}<span>/100</span></div>
            </div>
            <p class="cdx-score-desc">Scored from call transcripts &amp; agent notes as leads are worked.</p>
          </article>

          <!-- Best time to call -->
          <article class="cdx-card">
            <div class="cdx-card-title">Best time to call</div>
            <div
              class="cdx-best-row"
              :class="{ 'cdx-best-row--click': bestDayOfWeek }"
              :title="bestDayOfWeek ? 'Click to view these leads' : ''"
              @click="bestDayOfWeek && openDrilldown(bestDayOfWeek, 'DAY_OF_WEEK', 'leads')"
            >
              <span class="cdx-best-label">Day of week</span>
              <span class="cdx-best-value-wrap">
                <span class="cdx-best-value">{{ bestDayOfWeek?.label ?? '—' }}</span>
                <span class="cdx-best-rate tnum">{{ bestDayOfWeek?.connectRateLabel ?? '—' }} connect</span>
              </span>
            </div>
            <div
              class="cdx-best-row"
              :class="{ 'cdx-best-row--click': bestHourOfDay }"
              :title="bestHourOfDay ? 'Click to view these leads' : ''"
              @click="bestHourOfDay && openDrilldown(bestHourOfDay, 'HOUR_OF_DAY', 'leads')"
            >
              <span class="cdx-best-label">Hour of day</span>
              <span class="cdx-best-value-wrap">
                <span class="cdx-best-value">{{ bestHourOfDay?.label ?? '—' }}</span>
                <span class="cdx-best-rate tnum">{{ bestHourOfDay?.connectRateLabel ?? '—' }} connect</span>
              </span>
            </div>
            <div
              class="cdx-best-row"
              :class="{ 'cdx-best-row--click': weakestHourOfDay }"
              :title="weakestHourOfDay ? 'Click to view these leads' : ''"
              @click="weakestHourOfDay && openDrilldown(weakestHourOfDay, 'HOUR_OF_DAY', 'leads')"
            >
              <span class="cdx-best-label">Weakest window</span>
              <span class="cdx-best-value-wrap">
                <span class="cdx-best-value">{{ weakestHourOfDay?.label ?? '—' }}</span>
                <span class="cdx-best-rate cdx-best-rate--muted tnum">{{ weakestHourOfDay?.connectRateLabel ?? '—' }} connect</span>
              </span>
            </div>
          </article>
        </div>

        <div class="cdx-lower-grid">
          <!-- Agent leaderboard (admin) / Your performance (agent, strictly own data only) -->
          <article class="cdx-card">
            <div class="cdx-card-title">
              {{ isAdmin ? 'Agent leaderboard' : 'Your performance' }}
              <span v-if="isAdmin" class="cdx-card-title__sub">sorted by conversion</span>
            </div>

            <div v-if="isAdmin && topAgent" class="cdx-spotlight">
              <span class="cdx-avatar cdx-avatar--lg" :style="{ background: agentColor(0) }">{{ initials(topAgent.label) }}</span>
              <div class="cdx-spotlight-body">
                <div class="cdx-spotlight-line1">
                  <span aria-hidden="true">🏆</span>{{ topAgent.label }} is the top performer{{ dateRangeSuffix }}
                </div>
                <div class="cdx-spotlight-line2">
                  {{ topAgent.conversionRateLabel }} conversion
                  <template v-if="topAgent.rankMovement != null && topAgent.rankMovement > 0">
                    · <span class="cdx-spotlight-up">▲ moved up {{ topAgent.rankMovement }}</span>
                  </template>
                  <template v-if="topAgent.streakPeriods > 1">
                    · leading for {{ topAgent.streakPeriods }} periods running
                  </template>
                </div>
              </div>
            </div>

            <div class="cdx-table-wrap">
              <table class="cdx-table">
                <thead>
                  <tr>
                    <th v-if="isAdmin">Rank</th>
                    <th>Agent</th>
                    <th class="num">Total calls</th>
                    <th class="num">Unique calls</th>
                    <th>Connect rate</th>
                    <th class="num">Avg. score</th>
                    <th class="num">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(agent, i) in agentLeaderboard" :key="agent.id"
                    class="cdx-row--clickable"
                    :title="drillLoading ? '' : 'Click any cell to view leads · click the agent name for their full profile'"
                    @click="onLeaderboardCellClick($event, agent)"
                  >
                    <td v-if="isAdmin" data-col="rank">
                      <span v-if="i < 3" class="cdx-medal" :class="`cdx-medal--${['gold', 'silver', 'bronze'][i]}`">{{ i + 1 }}</span>
                      <span v-else class="cdx-rank-num">{{ i + 1 }}</span>
                    </td>
                    <td data-col="name">
                      <button type="button" class="cdx-agent-cell" @click.stop="openAgentProfile(agent)">
                        <span class="cdx-avatar" :style="{ background: agentColor(i) }">{{ initials(agent.label) }}</span>
                        {{ agent.label }}
                      </button>
                    </td>
                    <td class="num tnum cdx-cell--click" data-col="calls" @click.stop="openDrilldown(agent, 'AGENT', 'calls')">{{ agent.callsPlaced }}</td>
                    <td class="num tnum cdx-muted cdx-cell--click" data-col="unique" @click.stop="openDrilldown(agent, 'AGENT', 'leads')">{{ agent.uniqueLeadsCalled ?? '—' }}</td>
                    <td data-col="connect">
                      <span class="cdx-connect-track"><span class="cdx-connect-fill" :style="{ width: (agent.connectRate || 0) * 100 + '%' }" /></span>
                      <span class="tnum">{{ agent.connectRateLabel }}</span>
                    </td>
                    <td class="num tnum" data-col="score">{{ agent.avgLeadScoreLabel }}</td>
                    <td class="num tnum" :class="{ 'cdx-rank-lead': i === 0 }" data-col="conversion">
                      {{ agent.conversionRateLabel }}
                      <span
                        v-if="isAdmin && agent.rankMovement != null && agent.rankMovement !== 0"
                        class="cdx-rank-move"
                        :class="agent.rankMovement > 0 ? 'cdx-rank-move--up' : 'cdx-rank-move--down'"
                      >{{ agent.rankMovement > 0 ? '▲' : '▼' }}{{ Math.abs(agent.rankMovement) }}</span>
                      <span v-else-if="isAdmin && agent.rankMovement === 0" class="cdx-rank-move cdx-rank-move--flat">—</span>
                    </td>
                  </tr>
                  <tr v-if="!agentLeaderboard.length">
                    <td colspan="7" class="cdx-empty-cell">No agent activity yet in this range.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>

          <!-- Trend -->
          <article class="cdx-card cdx-trend-card">
            <div class="cdx-trend-head">
              <div class="cdx-card-title">Calls per day</div>
              <div class="cdx-daterange__presets">
                <button type="button" class="cdx-daterange__chip" :class="{ 'is-active': trendView === 'chart' }" @click="trendView = 'chart'">Chart</button>
                <button type="button" class="cdx-daterange__chip" :class="{ 'is-active': trendView === 'table' }" @click="trendView = 'table'">Table</button>
              </div>
            </div>
            <template v-if="trendView === 'chart'">
              <div class="cdx-trend-legend">
                <span><span class="cdx-dot" style="background: var(--cdx-accent)" />Placed</span>
                <span><span class="cdx-dot" style="background: var(--cdx-status-good)" />Connected</span>
              </div>
              <div class="cdx-chart-wrap" ref="trendWrapRef">
                <svg class="cdx-trend-svg" viewBox="0 0 460 210" preserveAspectRatio="none" @mousemove="onTrendHover" @mouseleave="trendHoverIndex = null">
                  <line v-for="g in 4" :key="g" class="cdx-gridline" :x1="6" :x2="454" :y1="10 + (180 / 3) * (g - 1)" :y2="10 + (180 / 3) * (g - 1)" />
                  <path :d="trendAreaPath" fill="var(--cdx-accent)" opacity="0.08" stroke="none" />
                  <path :d="trendPlacedPath" fill="none" stroke="var(--cdx-accent)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
                  <path :d="trendConnectedPath" fill="none" stroke="var(--cdx-status-good)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />
                  <circle v-if="trendHoverPoint" :cx="trendHoverPoint.x" :cy="trendHoverPoint.yPlaced" r="4" fill="var(--cdx-accent)" stroke="var(--cdx-surface)" stroke-width="2" />
                  <circle v-if="trendHoverPoint" :cx="trendHoverPoint.x" :cy="trendHoverPoint.yConnected" r="4" fill="var(--cdx-status-good)" stroke="var(--cdx-surface)" stroke-width="2" />
                  <line v-if="trendHoverPoint" class="cdx-crosshair" :x1="trendHoverPoint.x" :x2="trendHoverPoint.x" y1="10" y2="190" />
                  <text
                    v-for="t in trendTicks" :key="t.x"
                    class="cdx-trend-axis-label"
                    :x="t.x" y="204" :text-anchor="t.anchor"
                  >{{ t.label }}</text>
                </svg>
                <div v-if="trendHoverPoint" class="cdx-tooltip" :style="{ left: trendHoverPoint.pxX + 'px', top: trendHoverPoint.pxY + 'px' }">
                  <div class="cdx-tooltip__date">{{ trendHoverPoint.label }}</div>
                  <div class="cdx-tooltip__row"><span>Placed</span><span class="v tnum">{{ trendHoverPoint.placed }}</span></div>
                  <div class="cdx-tooltip__row"><span>Connected</span><span class="v tnum">{{ trendHoverPoint.connected }}</span></div>
                </div>
                <p v-if="!trendDays.length" class="cdx-empty">No daily data yet.</p>
              </div>
            </template>
            <template v-else>
              <div v-if="!trendDays.length" class="cdx-empty">No daily data yet.</div>
              <div v-else class="cdx-scroll-table">
                <table class="cdx-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th class="num">Placed</th>
                      <th class="num">Connected</th>
                      <th class="num">Unique leads</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="d in trendDays" :key="d.date" class="cdx-row--clickable" title="Click a cell to view leads" @click="openDrilldown(d, 'DAY', 'leads', 'calls')">
                      <td><strong>{{ trendDateLabel(d.date) }}</strong></td>
                      <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(d, 'DAY', 'calls', 'calls')">{{ d.callsPlaced }}</td>
                      <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(d, 'DAY', 'calls', 'calls', { call_outcome: 'connected' })">{{ d.callsConnected }}</td>
                      <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(d, 'DAY', 'leads', 'calls')">{{ d.uniqueLeadsCalled ?? '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </article>
        </div>
      </div>

      <!-- ============ HOURLY ============ -->
      <div v-show="activeTab === 'hourly'" class="cdx-panel">
        <div class="cdx-tile-row cdx-tile-row--stat">
          <article
            class="cdx-card cdx-stat-card cdx-card--click"
            title="Click to view all calls in this range"
            @click="openDrilldown({ label: 'All calls' }, 'TOTAL', 'calls', 'calls')"
          >
            <p class="cdx-stat-label">Calls in range</p>
            <p class="cdx-stat-value tnum">{{ hourlyStats.totalCalls }}</p>
          </article>
          <article
            class="cdx-card cdx-stat-card cdx-card--click"
            title="Click to view connected calls in this range"
            @click="openDrilldown({ label: 'Connected calls' }, 'TOTAL', 'calls', 'calls', { call_outcome: 'connected' })"
          >
            <p class="cdx-stat-label">Avg. connect rate</p>
            <p class="cdx-stat-value tnum">{{ hourlyStats.avgConnectLabel }}</p>
          </article>
          <article
            class="cdx-card cdx-stat-card cdx-card--click"
            :class="{ 'cdx-card--disabled': !hourlyStats.busiest }"
            title="Click to view leads called in this hour"
            @click="hourlyStats.busiest && openDrilldown(hourlyStats.busiest, 'HOUR_OF_DAY', 'calls', 'calls')"
          >
            <p class="cdx-stat-label">Busiest hour</p>
            <p class="cdx-stat-value tnum">{{ hourlyStats.busiest?.label ?? '—' }}</p>
            <p class="cdx-stat-sub">{{ hourlyStats.busiest?.callsPlaced ?? 0 }} calls placed</p>
          </article>
          <article
            class="cdx-card cdx-stat-card cdx-card--click"
            :class="{ 'cdx-card--disabled': !hourlyStats.weakest }"
            title="Click to view leads called in this hour"
            @click="hourlyStats.weakest && openDrilldown(hourlyStats.weakest, 'HOUR_OF_DAY', 'calls', 'calls')"
          >
            <p class="cdx-stat-label">Weakest hour</p>
            <p class="cdx-stat-value tnum">{{ hourlyStats.weakest?.label ?? '—' }}</p>
            <p class="cdx-stat-sub cdx-stat-sub--muted">{{ hourlyStats.weakest?.connectRateLabel ?? '—' }} connect</p>
          </article>
        </div>

        <article class="cdx-card">
          <div class="cdx-card-title">
            Performance by hour
            <span class="cdx-card-title__sub">bar height = calls placed · shade = {{ hourMetric === 'connect' ? 'connect rate' : 'conversion rate' }}</span>
          </div>
          <div class="cdx-hour-legend">
            <div class="cdx-metric-toggle">
              <button type="button" class="cdx-metric-btn" :class="{ 'is-active': hourMetric === 'connect' }" @click="hourMetric = 'connect'">Connect rate</button>
              <button type="button" class="cdx-metric-btn" :class="{ 'is-active': hourMetric === 'conversion' }" @click="hourMetric = 'conversion'">Conversion rate</button>
            </div>
            <span class="cdx-ramp" />
            <span class="cdx-ramp-labels"><span>Low</span><span>High</span></span>
            <span class="cdx-best-flag"><span class="cdx-best-dot" />Best hour</span>
          </div>
          <div class="cdx-hourchart-wrap" ref="hourWrapRef">
            <svg class="cdx-hourchart-svg" viewBox="0 0 900 190" preserveAspectRatio="none" @mousemove="onHourHover" @mouseleave="hourHoverIndex = null">
              <rect
                v-for="bar in hourlyBars" :key="bar.hour"
                :x="bar.x" :y="bar.y" :width="bar.width" :height="bar.height" rx="3"
                :fill="bar.color" :class="{ 'cdx-hourbar--best': bar.isBest }"
              />
              <text v-for="bar in hourAxisTicks" :key="`lbl-${bar.hour}`" :x="bar.x + bar.width / 2" y="184" class="cdx-hour-axis-label" text-anchor="middle">
                {{ bar.axisLabel }}
              </text>
            </svg>
            <div v-if="hourTooltip" class="cdx-tooltip" :style="{ left: hourTooltip.pxX + 'px', top: hourTooltip.pxY + 'px' }">
              <div class="cdx-tooltip__date">{{ hourTooltip.label }}</div>
              <div class="cdx-tooltip__row"><span>Calls placed</span><span class="v tnum">{{ hourTooltip.callsPlaced }}</span></div>
              <div class="cdx-tooltip__row"><span>Unique leads</span><span class="v tnum">{{ hourTooltip.uniqueLeadsCalled ?? '—' }}</span></div>
              <div class="cdx-tooltip__row"><span>Connect rate</span><span class="v tnum">{{ hourTooltip.connectRateLabel }}</span></div>
              <div class="cdx-tooltip__row"><span>Conversion rate</span><span class="v tnum">{{ hourTooltip.conversionRateLabel }}</span></div>
              <div class="cdx-tooltip__row"><span>Avg lead score</span><span class="v tnum">{{ hourTooltip.avgLeadScoreLabel }}</span></div>
            </div>
          </div>
        </article>

        <article class="cdx-card">
          <div class="cdx-card-title">Hour-by-hour breakdown <span class="cdx-card-title__sub">all 24 hours · scroll for more</span></div>
          <div class="cdx-scroll-table">
            <table class="cdx-table">
              <thead>
                <tr>
                  <th>Hour</th>
                  <th class="num">Calls placed</th>
                  <th class="num">Unique leads</th>
                  <th>Connect rate</th>
                  <th class="num">Conversion rate</th>
                  <th class="num">Avg. lead score</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="bucket in metricsHour?.buckets || []" :key="bucket.id"
                  class="cdx-row--clickable"
                  :class="{ 'cdx-row--best': bucket.hour === hourlyStats.busiest?.hour }"
                  title="Click any cell to view leads called in this hour"
                  @click="openDrilldown(bucket, 'HOUR_OF_DAY')"
                >
                  <td>{{ bucket.label }}</td>
                  <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(bucket, 'HOUR_OF_DAY', 'calls', 'calls')">{{ bucket.callsPlaced }}</td>
                  <td class="num tnum cdx-muted cdx-cell--click" @click.stop="openDrilldown(bucket, 'HOUR_OF_DAY', 'leads', 'calls')">{{ bucket.uniqueLeadsCalled ?? '—' }}</td>
                  <td class="cdx-cell--click" @click.stop="openDrilldown(bucket, 'HOUR_OF_DAY', 'calls', 'calls', { call_outcome: 'connected' })">
                    <span class="cdx-connect-track"><span class="cdx-connect-fill" :style="{ width: (bucket.connectRate || 0) * 100 + '%' }" /></span>
                    <span class="tnum">{{ bucket.connectRateLabel }}</span>
                  </td>
                  <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(bucket, 'HOUR_OF_DAY', 'leads')">{{ bucket.conversionRateLabel }}</td>
                  <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(bucket, 'HOUR_OF_DAY', 'leads')">{{ bucket.avgLeadScoreLabel }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </div>

      <!-- ============ BY STATUS ============ -->
      <div v-show="activeTab === 'status'" class="cdx-panel">
        <div class="cdx-tile-row cdx-tile-row--stat">
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Statuses tracked</p>
            <p class="cdx-stat-value tnum">{{ statusMetrics?.statuses.length ?? 0 }}</p>
          </article>
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Top status</p>
            <p class="cdx-stat-value tnum">{{ statusStats.top?.label ?? '—' }}</p>
            <p class="cdx-stat-sub">{{ statusStats.top?.count ?? 0 }} leads ({{ statusStats.top?.pctLabel ?? '—' }})</p>
          </article>
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Best converting status</p>
            <p class="cdx-stat-value tnum">{{ statusStats.bestConversion?.label ?? '—' }}</p>
            <p class="cdx-stat-sub">{{ statusStats.bestConversion?.conversionRateLabel ?? '—' }} conversion</p>
          </article>
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Avg. calls / lead</p>
            <p class="cdx-stat-value tnum">{{ statusStats.avgCallsPerLead }}</p>
          </article>
        </div>

        <article class="cdx-card">
          <div class="cdx-card-title">Status / dispo breakdown <span class="cdx-card-title__sub">all request_status values, sorted by count</span></div>
          <p v-if="statusMetrics?.snapshotMissing" class="cdx-empty">
            No saved snapshot for that date yet — historical breakdowns are only available from when this feature went live.
          </p>
          <div v-if="statusMetrics?.statuses.length" class="cdx-scroll-table">
            <table class="cdx-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th class="num">Count</th>
                  <th class="num">% of leads</th>
                  <th class="num">Total calls</th>
                  <th class="num">Unique calls</th>
                  <th class="num">Avg. lead score</th>
                  <th class="num">Conversion</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in statusMetrics.statuses" :key="row.id"
                  class="cdx-row--clickable"
                  title="Click any cell to view leads in this status"
                  @click="openLeadsByStatus(row.status, row.label)"
                >
                  <td><strong>{{ row.label }}</strong></td>
                  <td class="num tnum">{{ row.count }}</td>
                  <td class="num tnum">{{ row.pctLabel }}</td>
                  <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(row, 'STATUS', 'calls')">{{ row.totalCalls ?? '—' }}</td>
                  <td class="num tnum cdx-cell--click" @click.stop="openDrilldown(row, 'STATUS', 'leads')">{{ row.uniqueCalls ?? '—' }}</td>
                  <td class="num tnum">{{ row.avgLeadScoreLabel }}</td>
                  <td class="num tnum">{{ row.conversionRateLabel }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="cdx-total-row">
                  <td>Total</td>
                  <td class="num tnum">{{ statusMetrics.total }}</td>
                  <td class="num tnum cdx-muted">100%</td>
                  <td class="num tnum">{{ statusTotals.totalCalls }}</td>
                  <td class="num tnum">{{ statusTotals.uniqueCalls }}</td>
                  <td colspan="2" />
                </tr>
              </tfoot>
            </table>
          </div>
          <p v-else class="cdx-empty">No status data yet for this campaign.</p>
        </article>
      </div>
    </template>

    <!-- ============ DRILLDOWN (modal popup, matches the mockup) ============ -->
    <Teleport to="body">
      <div v-if="drillOpen" class="cdx-root cdx-modal-overlay" @click.self="closeDrilldown">
        <section class="cdx-modal-panel" role="dialog" aria-modal="true" :aria-label="drillTitle">
          <div class="cdx-modal-head">
            <div>
              <p class="cdx-eyebrow">{{ drillMode === 'agent' ? 'Agent profile' : 'Drilldown' }}</p>
              <h3 class="cdx-drill__title">{{ drillTitle }}</h3>
              <p v-if="drillSummary" class="cdx-drill__summary">{{ drillSummary }}</p>
            </div>
            <button type="button" class="cdx-icon-btn" aria-label="Close" @click="closeDrilldown">
              <v-icon icon="mdi-close" size="18" />
            </button>
          </div>
          <div class="cdx-modal-body">

      <template v-if="drillMode === 'agent' && drillAgent">
        <div class="cdx-tile-row cdx-tile-row--stat" style="margin-bottom: 14px">
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Total calls</p>
            <p class="cdx-stat-value tnum">{{ drillAgent.callsPlaced }}</p>
          </article>
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Unique calls</p>
            <p class="cdx-stat-value tnum">{{ drillAgent.uniqueLeadsCalled ?? '—' }}</p>
          </article>
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Connect rate</p>
            <p class="cdx-stat-value tnum">{{ drillAgent.connectRateLabel }}</p>
          </article>
          <article class="cdx-card cdx-stat-card">
            <p class="cdx-stat-label">Conversion</p>
            <p class="cdx-stat-value tnum">{{ drillAgent.conversionRateLabel }}</p>
          </article>
        </div>
        <article class="cdx-card" style="margin-bottom: 14px" v-if="drillAgent.statusCounts?.length">
          <div class="cdx-card-title">Status breakdown</div>
          <div class="cdx-segbar" role="img" aria-label="Agent status breakdown">
            <span
              v-for="(seg, i) in drillAgent.statusCounts"
              :key="seg.key"
              :style="{ width: (seg.count / drillAgentStatusTotal) * 100 + '%', background: catColor(i) }"
            />
          </div>
          <div class="cdx-legend">
            <div v-for="(seg, i) in drillAgent.statusCounts" :key="seg.key" class="cdx-legend-row">
              <span class="cdx-swatch" :style="{ background: catColor(i) }" />
              {{ seg.label }}
              <span class="cdx-legend-num tnum">{{ seg.count }}</span>
            </div>
          </div>
        </article>
        <article class="cdx-card" v-if="drillAgent.hourly?.length">
          <div class="cdx-card-title">Hourly pattern <span class="cdx-card-title__sub">calls placed by hour</span></div>
          <div class="cdx-hourchart-wrap" style="height: 110px">
            <svg class="cdx-hourchart-svg" viewBox="0 0 900 120" preserveAspectRatio="none">
              <rect
                v-for="bar in drillAgentHourlyBars" :key="bar.hour"
                :x="bar.x" :y="bar.y" :width="bar.width" :height="bar.height" rx="2"
                fill="var(--cdx-accent)" opacity="0.85"
              />
            </svg>
          </div>
        </article>
      </template>

      <template v-else>
        <p v-if="drillLoading" class="cdx-empty">Loading rows…</p>
        <p v-else-if="drillError" class="cdx-empty cdx-empty--error">{{ drillError }}</p>
        <p v-else-if="drillListMode === 'calls' && !drillCallRows.length" class="cdx-empty">No calls for this selection yet.</p>
        <p v-else-if="drillListMode === 'leads' && !drillLeadRows.length" class="cdx-empty">No linked leads for this selection yet.</p>
        <table v-else-if="drillListMode === 'calls'" class="cdx-table">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Phone</th>
              <th>Called on</th>
              <th class="num">Duration</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in drillCallRows" :key="row.callKey">
              <td><strong>{{ row.name }}</strong></td>
              <td class="tnum">{{ row.phone || '—' }}</td>
              <td class="tnum">{{ row.calledOnLabel || '—' }}</td>
              <td class="num tnum">{{ row.durationLabel || '—' }}</td>
              <td>{{ row.outcome || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <table v-else class="cdx-table">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Phone</th>
              <th>Status</th>
              <th class="num">Score</th>
              <th v-if="drillLeadRowsHaveOutcome">Last outcome</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in drillLeadRows" :key="row.id">
              <td><strong>{{ row.name }}</strong></td>
              <td class="tnum">{{ row.phone || '—' }}</td>
              <td><span v-if="row.statusLabel" class="cdx-pill">{{ row.statusLabel }}</span><template v-else>—</template></td>
              <td class="num tnum">{{ row.scoreLabel }}</td>
              <td v-if="drillLeadRowsHaveOutcome">{{ row.lastOutcome || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </template>

          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  fetchCampaignLeads,
  fetchCampaignMetrics,
  fetchCampaignMetricsDrilldown,
  fetchCampaignStatusMetrics,
  fetchScopeMetrics,
  fetchScopeMetricsDrilldown,
  fetchScopeStatusMetrics,
} from '@/api/campaignsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { normalizeCampaignLeadsList } from '@/utils/campaigns'
import { unwrapApiList } from '@/utils/graphql'
import {
  drilldownInputFromBucket,
  normalizeCampaignMetricsDrilldownResponse,
  normalizeCampaignMetricsResponse,
  normalizeCampaignStatusMetricsResponse,
} from '@/utils/campaignMetrics'

defineOptions({ name: 'CampaignDashboardPanel' })

const props = defineProps({
  campaignId: { type: String, default: '' },
  campaignName: { type: String, default: '' },
  convertedStatus: { type: String, default: 'CONVERTED' },
  active: { type: Boolean, default: true },
  showHeader: { type: Boolean, default: true },
  // The host page already shows the product/campaign name in its own page
  // title bar right above this panel -- repeating it here too (the
  // "PRODUCT DASHBOARD / <name>" eyebrow+title block) is pure duplication.
  // Default true keeps Campaign's own dashboard tab exactly as it was;
  // ContextDetailPage.vue passes false. The Refresh button (also inside
  // showHeader) stays either way -- it's the one thing this header actually
  // does that the page title bar doesn't.
  showTitle: { type: Boolean, default: true },
  // 'CAMPAIGN' (default) keeps every fetch call below exactly as it always
  // was -- single task_id, hitting getTaskMetrics/getTaskStatusMetrics/
  // getTaskMetricsDrilldown. 'PRODUCT'/'ORGANIZATION' route the SAME calls
  // through the generalized getScopeMetrics/getScopeStatusMetrics/
  // getScopeMetricsDrilldown commands instead, with campaignId reused as
  // "the scope id" (kept as one prop rather than renaming it everywhere
  // this component reads it).
  scopeType: { type: String, default: 'CAMPAIGN' },
  /**
   * The campaign's resolved leads (from the parent page's S3 shard join).
   * The drilldown API returns raw call/history rows keyed only by
   * request_id -- names/phones/current status come from joining against
   * this list client-side, same as the Leads tab itself does.
   */
  leads: { type: Array, default: () => [] },
  // Non-admin viewers (a plain agent looking at their own campaign) are
  // strictly scoped server-side to their own data -- this only changes
  // presentation (no "leaderboard"/other-agent comparison framing, no
  // org-wide all_leads fetch), never what's actually fetched.
  isAdmin: { type: Boolean, default: true },
})

// Fixed categorical order -- never cycled, matches the dashboard design's
// palette (purple/blue/green/red/amber/teal, then a neutral "Other" grey).
const CAT_COLORS = ['#9085e9', '#3987e5', '#199e70', '#e66767', '#d6a13d', '#3dbfc2']
const AGENT_COLORS = ['#3987e5', '#199e70', '#e66767', '#9085e9', '#d6a13d', '#3dbfc2']
function catColor(i) { return CAT_COLORS[i % CAT_COLORS.length] }
function agentColor(i) { return AGENT_COLORS[i % AGENT_COLORS.length] }
function outcomeColor(outcome) {
  const key = String(outcome || '').toLowerCase()
  if (key.includes('connect')) return 'var(--cdx-status-good)'
  if (key.includes('fail')) return 'var(--cdx-status-critical)'
  return 'var(--cdx-status-warning)'
}

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase()
}
function formatAgentLabel(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) return ''
  return raw.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase())
}

const TABS = [
  { value: 'overview', title: 'Overview' },
  { value: 'hourly', title: 'Hourly' },
  { value: 'status', title: 'By status' },
]
const activeTab = ref('overview')

// ---- Date range (mirrors the dashboard design: 7d/30d/Campaign/Custom) ----
const DATE_PRESETS = [
  { value: '7d', title: '7d' },
  { value: '30d', title: '30d' },
  { value: 'campaign', title: 'Campaign' },
  { value: 'custom', title: 'Custom' },
]
const datePreset = ref('campaign')
const customDateFrom = ref('')
const customDateTo = ref('')
function toDateStr(d) { return d.toISOString().slice(0, 10) }
const dateRange = computed(() => {
  const today = new Date()
  if (datePreset.value === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6)
    return { date_from: toDateStr(from), date_to: toDateStr(today) }
  }
  if (datePreset.value === '30d') {
    const from = new Date(today); from.setDate(from.getDate() - 29)
    return { date_from: toDateStr(from), date_to: toDateStr(today) }
  }
  if (datePreset.value === 'custom') {
    return { date_from: customDateFrom.value || undefined, date_to: customDateTo.value || undefined }
  }
  return { date_from: undefined, date_to: undefined }
})
const dateRangeLabel = computed(() => {
  const { date_from: from, date_to: to } = dateRange.value
  if (!from && !to) return 'Full campaign (live)'
  if (from && to) return `${from} – ${to}`
  return from || to || ''
})
const dateRangeSuffix = computed(() => (datePreset.value === 'campaign' ? '' : ' this period'))

function setDatePreset(value) {
  datePreset.value = value
  if (value !== 'custom') void loadAll()
}
function applyCustomRange() {
  if (customDateFrom.value && customDateTo.value) void loadAll()
}

// ---- Data loading: every granularity fetched together so all three tabs
// are ready the moment you switch, rather than lazy-loading per tab. ----
const loading = ref(false)
const errorMessage = ref('')
const metricsTotal = ref(null)
const metricsDay = ref(null)
const metricsHour = ref(null)
const metricsDow = ref(null)
const metricsAgent = ref(null)
const statusMetrics = ref(null)
const hasAnyData = computed(() => !!(metricsTotal.value || statusMetrics.value))

const isCampaignScope = computed(() => props.scopeType === 'CAMPAIGN')
const dashboardEyebrow = computed(() => {
  if (props.scopeType === 'PRODUCT') return 'Product dashboard'
  if (props.scopeType === 'ORGANIZATION') return 'Organization dashboard'
  return 'Campaign dashboard'
})

async function loadAll() {
  const scopeId = String(props.campaignId || '').trim()
  if (!scopeId) return
  loading.value = true
  errorMessage.value = ''
  try {
    const base = isCampaignScope.value
      ? { task_id: scopeId, converted_status: props.convertedStatus, ...dateRange.value }
      : { scope_type: props.scopeType, scope_id: scopeId, converted_status: props.convertedStatus, ...dateRange.value }
    const metricsFn = isCampaignScope.value ? fetchCampaignMetrics : fetchScopeMetrics
    const statusFn = isCampaignScope.value ? fetchCampaignStatusMetrics : fetchScopeStatusMetrics
    const [total, day, hour, dow, agent, status] = await Promise.all([
      metricsFn({ ...base, granularity: 'TOTAL' }),
      metricsFn({ ...base, granularity: 'DAY' }),
      metricsFn({ ...base, granularity: 'HOUR_OF_DAY' }),
      metricsFn({ ...base, granularity: 'DAY_OF_WEEK' }),
      metricsFn({ ...base, granularity: 'AGENT' }),
      statusFn(base),
    ])
    const results = { total, day, hour, dow, agent, status }
    const failed = Object.entries(results).find(([, r]) => !isApiSuccess(r))
    if (failed) {
      errorMessage.value = getApiMessage(failed[1], 'Failed to load campaign metrics')
    }
    metricsTotal.value = normalizeCampaignMetricsResponse(total, 'TOTAL')
    metricsDay.value = normalizeCampaignMetricsResponse(day, 'DAY')
    metricsHour.value = normalizeCampaignMetricsResponse(hour, 'HOUR_OF_DAY')
    metricsDow.value = normalizeCampaignMetricsResponse(dow, 'DAY_OF_WEEK')
    metricsAgent.value = normalizeCampaignMetricsResponse(agent, 'AGENT')
    statusMetrics.value = normalizeCampaignStatusMetricsResponse(status)
  } finally {
    loading.value = false
  }
}

// ---- Overview: lead status funnel ----
const totalBucket = computed(() => metricsTotal.value?.buckets?.[0] || null)
const metricsTotalRoot = computed(() => metricsTotal.value)
const showOtherStatuses = ref(false)
const MAX_VISIBLE_SEGMENTS = 5

function foldSegments(list) {
  const total = list.reduce((s, x) => s + x.count, 0)
  const visible = list.length <= MAX_VISIBLE_SEGMENTS ? list : list.slice(0, MAX_VISIBLE_SEGMENTS - 1)
  const other = list.length <= MAX_VISIBLE_SEGMENTS ? [] : list.slice(MAX_VISIBLE_SEGMENTS - 1)
  const otherCount = other.reduce((s, x) => s + x.count, 0)
  return {
    visible: visible.map((s, i) => ({ ...s, colorIndex: i, pct: total ? (s.count / total) * 100 : 0 })),
    other,
    otherCount,
    otherPct: total ? (otherCount / total) * 100 : 0,
    ariaLabel: `Breakdown: ${list.map((s) => `${s.count} ${s.label}`).join(', ')}`,
  }
}
// Lead status funnel reads from statusMetrics (the same as-of-date/live
// STATUS# counters the By Status tab uses), NOT totalBucket.statusCounts --
// that older field is an ADD-only transition-event counter from the
// lead_history stream (bump() in yandle_metrics_aggregator), so a lead that
// cycled through "Contacted" twice counts twice there even though it's no
// longer Contacted. STATUS#/STATUS_SNAPSHOT# (getTaskStatusMetrics) is a
// true current-state distinct-lead count, which is what "leads in campaign"
// actually means and what makes a click-to-drilldown count match exactly.
const leadStatusSegments = computed(() => foldSegments(
  (statusMetrics.value?.statuses || []).map((s) => ({ ...s, key: s.id })),
))
const totalLeadsInFunnel = computed(() => statusMetrics.value?.total || 0)

const callOutcomeSegments = computed(() => {
  const list = totalBucket.value?.callOutcomes || []
  const total = list.reduce((s, x) => s + x.count, 0)
  return list.map((s) => ({ ...s, pct: total ? (s.count / total) * 100 : 0 }))
})

// Short/medium/long, connected calls only -- same shape/derivation as
// callOutcomeSegments above, just off callDurations instead of callOutcomes.
const callDurationSegments = computed(() => {
  const list = totalBucket.value?.callDurations || []
  const total = list.reduce((s, x) => s + x.count, 0)
  return list.map((s, i) => ({ ...s, pct: total ? (s.count / total) * 100 : 0, colorIndex: i }))
})

const scoreRingPct = computed(() => {
  const score = totalBucket.value?.avgLeadScore
  return score == null ? 0 : Math.min(1, Math.max(0, score / 100))
})

const bestDayOfWeek = computed(() => metricsDow.value?.best || null)
const bestHourOfDay = computed(() => metricsHour.value?.best || null)
const weakestHourOfDay = computed(() => {
  const buckets = (metricsHour.value?.buckets || []).filter((b) => b.connectRate != null)
  if (!buckets.length) return null
  return [...buckets].sort((a, b) => a.connectRate - b.connectRate)[0]
})

// ---- Overview: agent leaderboard ----
const agentLeaderboard = computed(() => {
  const buckets = metricsAgent.value?.buckets || []
  return [...buckets].sort((a, b) => (b.conversionRate ?? -1) - (a.conversionRate ?? -1) || (b.connectRate ?? -1) - (a.connectRate ?? -1))
})
const topAgent = computed(() => agentLeaderboard.value[0] || null)

// ---- Overview: trend chart (SVG paths computed reactively) ----
const trendView = ref('chart')
const trendWrapRef = ref(null)
const trendHoverIndex = ref(null)
const TW = 460, THt = 210, TPadL = 6, TPadR = 6, TPadT = 10, TPadB = 20
const trendDays = computed(() => [...(metricsDay.value?.buckets || [])].sort((a, b) => a.date.localeCompare(b.date)))
const trendMax = computed(() => Math.max(1, ...trendDays.value.map((d) => d.callsPlaced)) * 1.15)
function trendX(i) {
  const n = trendDays.value.length
  return n <= 1 ? TPadL : TPadL + (i / (n - 1)) * (TW - TPadL - TPadR)
}
function trendY(v) {
  return TPadT + (THt - TPadT - TPadB) - (v / trendMax.value) * (THt - TPadT - TPadB)
}
function trendDateLabel(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`)
  if (Number.isNaN(d.getTime())) return dateStr
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d)
}
// Selective, evenly-spaced ticks (never one label per point) so the axis
// stays legible whether the range is 7 days or a full campaign's worth.
const trendTicks = computed(() => {
  const n = trendDays.value.length
  if (!n) return []
  const maxTicks = Math.min(n, 6)
  const step = maxTicks > 1 ? (n - 1) / (maxTicks - 1) : 0
  const seen = new Set()
  const ticks = []
  for (let k = 0; k < maxTicks; k++) {
    const i = Math.round(k * step)
    if (seen.has(i)) continue
    seen.add(i)
    const anchor = i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'
    ticks.push({ x: trendX(i), label: trendDateLabel(trendDays.value[i].date), anchor })
  }
  return ticks
})
function trendPathFor(key) {
  return trendDays.value.map((d, i) => `${i === 0 ? 'M' : 'L'}${trendX(i).toFixed(1)},${trendY(d[key]).toFixed(1)}`).join(' ')
}
const trendPlacedPath = computed(() => trendPathFor('callsPlaced'))
const trendConnectedPath = computed(() => trendPathFor('callsConnected'))
const trendAreaPath = computed(() => {
  if (!trendDays.value.length) return ''
  const base = THt - TPadB
  return `${trendPlacedPath.value} L${trendX(trendDays.value.length - 1).toFixed(1)},${base} L${trendX(0).toFixed(1)},${base} Z`
})
function onTrendHover(evt) {
  const rect = evt.currentTarget.getBoundingClientRect()
  const relX = ((evt.clientX - rect.left) / rect.width) * TW
  const n = trendDays.value.length
  if (!n) return
  let i = Math.round(((relX - TPadL) / (TW - TPadL - TPadR)) * (n - 1))
  i = Math.max(0, Math.min(n - 1, i))
  trendHoverIndex.value = i
}
const trendHoverPoint = computed(() => {
  const i = trendHoverIndex.value
  if (i == null || !trendDays.value[i]) return null
  const d = trendDays.value[i]
  const rect = trendWrapRef.value?.getBoundingClientRect()
  const pxRatio = rect ? rect.width / TW : 1
  const pxRatioY = rect ? rect.height / THt : 1
  return {
    x: trendX(i), yPlaced: trendY(d.callsPlaced), yConnected: trendY(d.callsConnected),
    pxX: trendX(i) * pxRatio, pxY: trendY(d.callsPlaced) * pxRatioY,
    label: d.date, placed: d.callsPlaced, connected: d.callsConnected,
  }
})

// ---- Hourly tab ----
const hourMetric = ref('connect')
const hourWrapRef = ref(null)
const hourHoverIndex = ref(null)
const HW = 900, HH = 190, HPadL = 4, HPadR = 4, HPadT = 6, HPadB = 26, HGap = 4

const hourlyStats = computed(() => {
  const buckets = metricsHour.value?.buckets || []
  const totalCalls = buckets.reduce((s, b) => s + b.callsPlaced, 0)
  const active = buckets.filter((b) => b.connectRate != null)
  const avgConnect = active.length ? active.reduce((s, b) => s + b.connectRate, 0) / active.length : null
  const busiest = buckets.reduce((best, b) => (!best || b.callsPlaced > best.callsPlaced ? b : best), null)
  const weakest = active.length ? [...active].sort((a, b) => a.connectRate - b.connectRate)[0] : null
  return {
    totalCalls,
    avgConnectLabel: avgConnect == null ? '—' : `${Math.round(avgConnect * 100)}%`,
    busiest, weakest,
  }
})

function hourBarLayout(buckets, metricKey) {
  const n = buckets.length || 24
  const plotW = HW - HPadL - HPadR, plotH = HH - HPadT - HPadB
  const barW = (plotW / n) - HGap
  const maxPlaced = Math.max(1, ...buckets.map((b) => b.callsPlaced))
  const values = buckets.map((b) => b[metricKey]).filter((v) => v != null && v > 0)
  const minV = values.length ? Math.min(...values) : 0
  const maxV = Math.max(0, ...buckets.map((b) => b[metricKey] ?? 0))
  const bestIdx = buckets.reduce((best, b, i) => ((b[metricKey] ?? -1) > (buckets[best]?.[metricKey] ?? -1) ? i : best), 0)
  return buckets.map((b, i) => {
    const h = (b.callsPlaced / maxPlaced) * plotH
    const x = HPadL + i * (plotW / n) + HGap / 2
    const t = maxV === minV ? 0.5 : Math.min(1, Math.max(0, ((b[metricKey] ?? 0) - minV) / (maxV - minV)))
    return {
      hour: b.hour, x, y: HPadT + (plotH - h), width: Math.max(barW, 1), height: Math.max(h, 0),
      color: `color-mix(in srgb, var(--cdx-accent) ${Math.round(20 + t * 80)}%, var(--cdx-surface-2))`,
      isBest: i === bestIdx,
      axisLabel: `${b.hour % 12 === 0 ? 12 : b.hour % 12}${b.hour < 12 ? 'A' : 'P'}`,
    }
  })
}
const hourlyBars = computed(() => hourBarLayout(metricsHour.value?.buckets || [], hourMetric.value === 'connect' ? 'connectRate' : 'conversionRate'))
// Buckets here are only the hours with actual activity, not a fixed 0-23
// sweep -- they can be as few as 2-3 bars or as many as 24, so a fixed
// "every 3rd bar" tick was either way too sparse (a handful of active hours)
// or unreadably dense (a full day). Pick evenly spaced ticks off however
// many bars there actually are, same approach as the daily trend chart.
const hourAxisTicks = computed(() => {
  const bars = hourlyBars.value
  const n = bars.length
  if (!n) return []
  const maxTicks = Math.min(n, 8)
  const step = maxTicks > 1 ? (n - 1) / (maxTicks - 1) : 0
  const seen = new Set()
  const ticks = []
  for (let k = 0; k < maxTicks; k++) {
    const i = Math.round(k * step)
    if (seen.has(i)) continue
    seen.add(i)
    ticks.push(bars[i])
  }
  return ticks
})

function onHourHover(evt) {
  const rect = evt.currentTarget.getBoundingClientRect()
  const relX = ((evt.clientX - rect.left) / rect.width) * HW
  const n = (metricsHour.value?.buckets || []).length || 24
  let i = Math.floor((relX - HPadL) / ((HW - HPadL - HPadR) / n))
  i = Math.max(0, Math.min(n - 1, i))
  hourHoverIndex.value = i
}
const hourTooltip = computed(() => {
  const i = hourHoverIndex.value
  const bucket = metricsHour.value?.buckets?.[i]
  const bar = hourlyBars.value[i]
  if (i == null || !bucket || !bar) return null
  const rect = hourWrapRef.value?.getBoundingClientRect()
  const pxRatio = rect ? rect.width / HW : 1
  const pxRatioY = rect ? rect.height / HH : 1
  return { ...bucket, pxX: (bar.x + bar.width / 2) * pxRatio, pxY: bar.y * pxRatioY }
})

// ---- By status tab ----
const statusStats = computed(() => {
  const rows = statusMetrics.value?.statuses || []
  const total = statusMetrics.value?.total || 0
  const top = rows[0] || null
  const bestConversion = rows.filter((r) => r.conversionRate != null).sort((a, b) => b.conversionRate - a.conversionRate)[0] || null
  const totalCalls = rows.reduce((s, r) => s + (r.totalCalls || 0), 0)
  return {
    top, bestConversion,
    avgCallsPerLead: total ? (totalCalls / total).toFixed(1) : '0.0',
  }
})

// Each lead has exactly one CURRENT status, so summing uniqueCalls across
// status rows double-counts nothing -- it's the same distinct-lead total the
// Overview tab's Call Outcomes tile shows.
const statusTotals = computed(() => {
  const rows = statusMetrics.value?.statuses || []
  return {
    totalCalls: rows.reduce((s, r) => s + (r.totalCalls || 0), 0),
    uniqueCalls: rows.reduce((s, r) => s + (r.uniqueCalls || 0), 0),
  }
})

// ---- Drilldown (leads list, or an agent's combined profile) ----
const drillOpen = ref(false)
const drillMode = ref('leads') // 'leads' | 'agent'
const drillLoading = ref(false)
const drillError = ref('')
const drillTitle = ref('')
const drillItems = ref([])
const drillTotalCalls = ref(0)
const drillTotalHistory = ref(0)
const drillAgent = ref(null)
const drillListMode = ref('leads')

const drillSummary = computed(() => {
  if (!drillOpen.value || drillLoading.value || drillMode.value === 'agent') return ''
  const parts = []
  if (drillTotalCalls.value) parts.push(`${drillTotalCalls.value} call${drillTotalCalls.value === 1 ? '' : 's'}`)
  if (drillTotalHistory.value) parts.push(`${drillTotalHistory.value} status update${drillTotalHistory.value === 1 ? '' : 's'}`)
  if (!parts.length && drillItems.value.length) parts.push(`${drillItems.value.length} row${drillItems.value.length === 1 ? '' : 's'}`)
  return parts.join(' · ')
})

// allLeadsForDrilldown holds the dedicated, unscoped (all_leads: true) roster
// fetched by openLeadsByStatus below -- merged in here (on top of props.leads)
// so every join in this file, not just the status drilldown, benefits from
// having the complete lead set rather than whatever the Leads tab's current
// scope/filter happens to be showing.
const allLeadsForDrilldown = ref([])
const leadById = computed(() => {
  const map = new Map()
  for (const lead of allLeadsForDrilldown.value) {
    const id = String(lead?.requestId ?? lead?.request_id ?? '').trim()
    if (id) map.set(id, lead)
  }
  for (const lead of props.leads || []) {
    const id = String(lead?.requestId ?? lead?.request_id ?? '').trim()
    if (id) map.set(id, lead)
  }
  return map
})

// One row PER LEAD (the popup design), not one per call/history event --
// the drilldown API returns raw events keyed by request_id only, so name/
// phone/current status come from joining against the parent page's already-
// loaded lead list; event fields fill the gaps (e.g. latest call outcome).
const drillLeadRows = computed(() => {
  const byLead = new Map()
  for (const item of drillItems.value) {
    const key = item.requestId || item.id
    const row = byLead.get(key) || { id: key, items: [] }
    row.items.push(item)
    byLead.set(key, row)
  }
  return [...byLead.values()].map((row) => {
    const lead = leadById.value.get(row.id) || {}
    // drillItems are already newest-first (normalize sorts by calledOn desc)
    const latestCall = row.items.find((i) => i.kind === 'call')
    const latestStatus = row.items.find((i) => i.status)
    const status = String(lead.status ?? lead.request_status ?? latestStatus?.status ?? '').trim()
    const score = lead.leadScore ?? lead.lead_score ?? row.items.find((i) => i.leadScore != null)?.leadScore
    return {
      id: row.id,
      name: pickDisplayName(lead.name, lead.contact_name, row.items.find((i) => i.name && i.name !== 'Lead')?.name) || 'Lead',
      phone: String(lead.phone ?? lead.phoneRaw ?? lead.contact_number ?? row.items.find((i) => i.phone)?.phone ?? '').trim(),
      status,
      statusLabel: status ? formatAgentLabel(status) || status : '',
      scoreLabel: score != null && Number.isFinite(Number(score)) ? String(score) : '—',
      lastOutcome: latestCall?.callStatusLabel || '',
    }
  })
})

// Status-based drilldowns (openLeadsByStatus) build rows straight off the
// lead roster with no call/history event joined in, so lastOutcome is always
// empty there -- showing a column of nothing but "—" implies a call that
// never happened. Hide it whenever no row actually has one.
const drillLeadRowsHaveOutcome = computed(() => drillLeadRows.value.some((r) => r.lastOutcome))

// One row PER CALL EVENT (repeats included) -- this is what "Total calls"
// means, as opposed to drillLeadRows' one-row-per-lead dedupe for "Unique calls".
const drillCallRows = computed(() => {
  return drillItems.value
    .filter((item) => item.kind === 'call')
    .map((item, index) => {
      const lead = leadById.value.get(item.requestId || item.id) || {}
      return {
        callKey: `${item.requestId || item.id || 'row'}-${item.calledOn || index}`,
        name: pickDisplayName(lead.name, lead.contact_name, item.name && item.name !== 'Lead' ? item.name : '') || 'Lead',
        phone: String(lead.phone ?? lead.phoneRaw ?? lead.contact_number ?? item.phone ?? '').trim(),
        calledOnLabel: item.calledOnLabel || '',
        durationLabel: item.durationLabel || '',
        outcome: item.callStatusLabel || '',
      }
    })
})

function pickDisplayName(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim()
    if (text && text !== '—') return text
  }
  return ''
}

const drillAgentStatusTotal = computed(() => (drillAgent.value?.statusCounts || []).reduce((s, x) => s + x.count, 0) || 1)
const drillAgentHourlyBars = computed(() => {
  const hourly = drillAgent.value?.hourly || []
  if (!hourly.length) return []
  const max = Math.max(1, ...hourly.map((h) => h.callsPlaced))
  const n = 24
  const plotW = HW - HPadL - HPadR, plotH = 120 - 4
  const barW = (plotW / n) - HGap
  const byHour = new Map(hourly.map((h) => [h.hour, h]))
  return Array.from({ length: n }, (_, hour) => {
    const h = byHour.get(hour)
    const height = h ? (h.callsPlaced / max) * plotH : 0
    return { hour, x: HPadL + hour * (plotW / n) + HGap / 2, y: plotH - height, width: Math.max(barW, 1), height: Math.max(height, 0) }
  })
})

function openAgentProfile(agent) {
  drillOpen.value = true
  drillMode.value = 'agent'
  drillTitle.value = agent.label
  drillAgent.value = agent
}

// Every cell in the leaderboard row is clickable -- the name cell has its
// own button (opens the richer combined profile, handled separately via
// @click.stop so this handler never double-fires for it); every other cell
// in the row opens the same "leads worked by this agent" drilldown, since
// the backend has no finer-grained filter per individual stat column.
function onLeaderboardCellClick(evt, agent) {
  if (evt.target.closest('button')) return
  void openDrilldown(agent, 'AGENT')
}

function normalizeStatusKey(value) {
  return String(value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_')
}

// Lead-status counts (leadStatusSegments/totalLeadsInFunnel) come from
// statusMetrics, the same current-state STATUS# counters getTaskStatusMetrics
// computes for the whole task. props.leads is NOT reliably that same set --
// it's whatever the Leads tab last loaded, which can be scoped to "My leads"
// or a paginated/filtered view -- so filtering it client-side silently
// returned an empty or partial list whenever that scope didn't match. Fixed
// by fetching a dedicated, unscoped roster (all_leads: true) here instead of
// trusting whatever the parent page happens to have loaded, and caching it
// per campaign so repeat clicks in the same session don't refetch.
let allLeadsCacheCampaignId = ''
async function fetchAllLeadsForDrilldown() {
  const taskId = String(props.campaignId || '').trim()
  if (allLeadsForDrilldown.value.length && allLeadsCacheCampaignId === taskId) return allLeadsForDrilldown.value
  // fetchCampaignLeads/getTaskLeads is single-task only -- at Product/
  // Organization scope, campaignId is a scope id (a context_id/org_id), not
  // a real task_id, so this call isn't meaningful there yet (no
  // multi-campaign leads-roster endpoint exists). The lead-status funnel's
  // "view leads" click-through is a known gap at scope level for now; the
  // numeric bucket drilldowns (openDrilldown below) are unaffected, since
  // those go through getScopeMetricsDrilldown instead.
  if (!isCampaignScope.value) {
    allLeadsForDrilldown.value = props.leads || []
    allLeadsCacheCampaignId = taskId
    return allLeadsForDrilldown.value
  }
  // all_leads: true is an org-manager-only request server-side (getTaskLeads
  // rejects it from a plain agent) -- for a non-admin viewer, omitting it
  // routes to the assigned-only path instead, which is exactly the strict
  // "only your own leads" scope a plain agent must get here.
  const response = await fetchCampaignLeads(
    props.isAdmin ? { task_id: taskId, all_leads: true } : { task_id: taskId },
  )
  const list = isApiSuccess(response) ? normalizeCampaignLeadsList(unwrapApiList(response)) : (props.leads || [])
  allLeadsForDrilldown.value = list
  allLeadsCacheCampaignId = taskId
  return list
}

async function openLeadsByStatus(status, label) {
  drillOpen.value = true
  drillMode.value = 'leads'
  drillListMode.value = 'leads'
  drillTitle.value = label || status || 'Leads'
  drillLoading.value = true
  drillError.value = ''
  drillItems.value = []
  drillTotalCalls.value = 0
  drillTotalHistory.value = 0
  try {
    const allLeads = await fetchAllLeadsForDrilldown()
    const target = status ? normalizeStatusKey(status) : null
    drillItems.value = allLeads
      .filter((lead) => !target || normalizeStatusKey(lead.status ?? lead.request_status) === target)
      .map((lead) => ({ requestId: lead.requestId ?? lead.request_id, id: lead.requestId ?? lead.request_id, kind: 'status' }))
  } finally {
    drillLoading.value = false
  }
}

async function openDrilldown(bucket, granularity, listMode = 'leads', sourceOverride = null, extraInput = null) {
  const scopeId = String(props.campaignId || '').trim()
  if (!scopeId || !bucket) return

  drillOpen.value = true
  drillMode.value = 'leads'
  drillListMode.value = listMode
  drillTitle.value = bucket.label || bucket.status || 'Selected'
  drillLoading.value = true
  drillError.value = ''
  drillItems.value = []
  drillTotalCalls.value = 0
  drillTotalHistory.value = 0

  try {
    // dateRange (the page-level 7d/30d/Campaign/Custom picker) is spread
    // FIRST so a bucket's own date_from/date_to (e.g. a single DAY row, or
    // STATUS's deliberate lack of a date filter) always wins over it --
    // reversing this order silently let the page-level range override a
    // specific day's drilldown back to the whole selected range.
    const scopeInput = isCampaignScope.value
      ? { task_id: scopeId }
      : { scope_type: props.scopeType, scope_id: scopeId }
    const drilldownFn = isCampaignScope.value ? fetchCampaignMetricsDrilldown : fetchScopeMetricsDrilldown
    const response = await drilldownFn({
      ...scopeInput,
      source: sourceOverride || (granularity === 'OUTCOME' || granularity === 'DURATION' ? 'calls' : 'both'),
      ...(granularity !== 'STATUS' ? dateRange.value : {}),
      ...drilldownInputFromBucket(bucket, granularity),
      ...(extraInput || {}),
    })
    if (!isApiSuccess(response)) {
      drillError.value = getApiMessage(response, 'Failed to load drilldown')
      return
    }
    const normalized = normalizeCampaignMetricsDrilldownResponse(response)
    drillItems.value = normalized.items
    drillTotalCalls.value = normalized.totalCalls || 0
    drillTotalHistory.value = normalized.totalHistory || 0
  } finally {
    drillLoading.value = false
  }
}

function closeDrilldown() {
  drillOpen.value = false
  drillMode.value = 'leads'
  drillError.value = ''
  drillItems.value = []
  drillTitle.value = ''
  drillTotalCalls.value = 0
  drillTotalHistory.value = 0
  drillAgent.value = null
}

watch(
  () => [props.active, props.campaignId, props.scopeType],
  ([active]) => {
    if (!active) { closeDrilldown(); return }
    void loadAll()
  },
  { immediate: true },
)

function onKeydown(evt) {
  if (evt.key === 'Escape' && drillOpen.value) closeDrilldown()
}
onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))

defineExpose({ loadMetrics: loadAll, closeDrilldown })
</script>

<style scoped>
/* Every value below is the app's OWN theme token (src/utils/portalTheme.js),
   not a hardcoded palette -- Vuetify swaps --v-theme-* on the root when the
   user toggles light/dark, so these follow automatically. Keeping the same
   --cdx-* names lets every rule elsewhere in this file stay unchanged. */
.cdx-root {
  --cdx-page: rgb(var(--v-theme-background));
  --cdx-surface: rgb(var(--v-theme-surface));
  --cdx-surface-2: rgb(var(--v-theme-background-subtle));
  --cdx-border: rgba(var(--v-theme-border), 0.6);
  --cdx-border-strong: rgb(var(--v-theme-border-strong));
  --cdx-ink: rgb(var(--v-theme-on-background));
  --cdx-ink-soft: rgb(var(--v-theme-on-surface));
  --cdx-ink-muted: rgb(var(--v-theme-text-muted));
  --cdx-ink-faint: rgba(var(--v-theme-text-muted), 0.75);
  --cdx-accent: rgb(var(--v-theme-accent));
  --cdx-accent-soft: rgba(var(--v-theme-accent), 0.14);
  --cdx-cat-other: rgb(var(--v-theme-secondary));
  --cdx-status-good: rgb(var(--v-theme-success));
  --cdx-status-warning: rgb(var(--v-theme-warning));
  --cdx-status-critical: rgb(var(--v-theme-error));
  --cdx-grid-line: rgba(var(--v-theme-border-strong), 0.35);

  color: var(--cdx-ink);
  font-variant-numeric: tabular-nums;
}

.cdx-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 1rem; margin-bottom: 16px;
}
.cdx-eyebrow { margin: 0; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--cdx-ink-faint); }
.cdx-title { margin: 3px 0 0; font-size: 20px; font-weight: 700; letter-spacing: -0.01em; color: var(--cdx-ink); }
.cdx-refresh {
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid var(--cdx-border); border-radius: 10px;
  padding: 7px 13px; background: var(--cdx-accent-soft); color: var(--cdx-accent);
  font: inherit; font-size: 12.5px; font-weight: 650; cursor: pointer;
}
.cdx-refresh:disabled { opacity: 0.5; cursor: default; }

.cdx-toolbar {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
  border-bottom: 1px solid var(--cdx-border);
  margin-bottom: 20px;
}
.cdx-tabs { display: flex; gap: 4px; }
.cdx-tab {
  appearance: none; background: none; border: none;
  font: inherit; font-size: 13.5px; font-weight: 600;
  color: var(--cdx-ink-faint); cursor: pointer;
  padding: 9px 4px 11px; margin-right: 20px;
  border-bottom: 2px solid transparent;
  transition: color 0.12s ease, border-color 0.12s ease;
}
.cdx-tab:hover { color: var(--cdx-ink-soft); }
.cdx-tab.is-active { color: var(--cdx-ink); border-bottom-color: var(--cdx-accent); }

.cdx-daterange { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding-bottom: 12px; }
.cdx-daterange__presets { display: inline-flex; gap: 2px; background: var(--cdx-surface-2); border: 1px solid var(--cdx-border); border-radius: 7px; padding: 2px; }
.cdx-daterange__chip {
  appearance: none; border: none; background: none;
  font: inherit; font-size: 12px; font-weight: 600;
  color: var(--cdx-ink-faint); cursor: pointer;
  padding: 5px 11px; border-radius: 5px;
}
.cdx-daterange__chip.is-active { background: var(--cdx-surface); color: var(--cdx-ink); box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); }
.cdx-daterange__custom { display: inline-flex; align-items: center; gap: 6px; }
.cdx-daterange__input {
  font: inherit; font-size: 12px; color: var(--cdx-ink);
  background: var(--cdx-surface-2); border: 1px solid var(--cdx-border);
  border-radius: 6px; padding: 5px 8px; color-scheme: dark;
}
.cdx-daterange__sep { color: var(--cdx-ink-faint); font-size: 12px; }
.cdx-daterange__label { font-size: 12px; color: var(--cdx-ink-muted); white-space: nowrap; }

.cdx-panel { display: flex; flex-direction: column; gap: 20px; }

.cdx-tile-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
@media (max-width: 1100px) { .cdx-tile-row { grid-template-columns: repeat(2, 1fr); } }

.cdx-card { background: var(--cdx-surface); border: 1px solid var(--cdx-border); border-radius: 14px; padding: 18px; min-width: 0; }
.cdx-card-title {
  font-size: 12.5px; font-weight: 600; color: var(--cdx-ink-muted);
  margin-bottom: 14px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.cdx-card-title__sub { font-weight: 500; color: var(--cdx-ink-faint); text-transform: none; }

.cdx-funnel-total { font-size: 26px; font-weight: 700; margin-bottom: 3px; }
.cdx-funnel-total--click { cursor: pointer; display: inline-block; border-radius: 6px; transition: color 0.1s ease; }
.cdx-funnel-total--click:hover { color: var(--cdx-accent); }
.cdx-funnel-total-label { font-size: 12px; color: var(--cdx-ink-faint); margin: 0 0 14px; }
.cdx-outcome-sub { display: flex; justify-content: space-between; align-items: baseline; font-size: 12px; color: var(--cdx-ink-faint); margin-bottom: 14px; }
.cdx-outcome-sub strong { color: var(--cdx-ink-soft); }
.cdx-outcome-sub__click { cursor: pointer; border-radius: 4px; }
.cdx-outcome-sub__click:hover { color: var(--cdx-ink); }
.cdx-outcome-sub__click:hover strong { color: var(--cdx-accent); }

.cdx-segbar { display: flex; height: 10px; border-radius: 6px; overflow: hidden; background: var(--cdx-surface-2); gap: 2px; }
.cdx-segbar > span { display: block; height: 100%; }
.cdx-legend { display: flex; flex-direction: column; gap: 7px; margin-top: 13px; }
.cdx-legend-row { display: flex; align-items: center; font-size: 12.5px; color: var(--cdx-ink-soft); }
.cdx-swatch { width: 8px; height: 8px; border-radius: 2px; margin-right: 8px; flex: none; }
.cdx-legend-num { margin-left: auto; color: var(--cdx-ink); font-weight: 600; }
.cdx-legend-row--other { cursor: pointer; user-select: none; }
.cdx-legend-row--other:hover { color: var(--cdx-ink); }
.cdx-caret { margin-left: 6px; color: var(--cdx-ink-faint); font-size: 10px; transition: transform 0.12s ease; }
.cdx-legend-row--other.is-open .cdx-caret { transform: rotate(90deg); }
.cdx-legend-detail { display: flex; flex-direction: column; gap: 5px; margin: 2px 0 2px 16px; padding-left: 8px; border-left: 1px solid var(--cdx-border); }
.cdx-legend-detail .cdx-legend-row { font-size: 11.5px; color: var(--cdx-ink-muted); }
.cdx-legend-row--click { cursor: pointer; margin: 0 -6px; padding: 3px 6px; border-radius: 6px; transition: background 0.1s ease; }
.cdx-legend-row--click:hover { background: color-mix(in srgb, var(--cdx-accent) 10%, transparent); color: var(--cdx-ink); }

.cdx-card--click { cursor: pointer; transition: box-shadow 0.12s ease; }
.cdx-card--click:hover { box-shadow: 0 0 0 1px var(--cdx-accent) inset; }
.cdx-card--disabled { cursor: default; }
.cdx-card--disabled:hover { box-shadow: none; }

.cdx-score-wrap { display: flex; align-items: center; gap: 16px; }
.cdx-score-value { font-size: 30px; font-weight: 700; }
.cdx-score-value span { font-size: 15px; color: var(--cdx-ink-faint); font-weight: 500; }
.cdx-score-desc { font-size: 12.5px; color: var(--cdx-ink-muted); margin: 6px 0 0; line-height: 1.5; }

.cdx-best-row { display: flex; justify-content: space-between; align-items: center; padding: 9px 0; border-top: 1px solid var(--cdx-border); }
.cdx-best-row:first-of-type { border-top: none; padding-top: 2px; }
.cdx-best-row--click { cursor: pointer; margin: 0 -8px; padding-left: 8px; padding-right: 8px; border-radius: 6px; transition: background 0.1s ease; }
.cdx-best-row--click:hover { background: color-mix(in srgb, var(--cdx-accent) 10%, transparent); }
.cdx-best-label { font-size: 12.5px; color: var(--cdx-ink-muted); }
.cdx-best-value-wrap { text-align: right; }
.cdx-best-value { display: block; font-size: 14.5px; font-weight: 650; }
.cdx-best-rate { font-size: 11.5px; color: var(--cdx-status-good); font-weight: 600; }
.cdx-best-rate--muted { color: var(--cdx-ink-faint); }

.cdx-lower-grid { display: grid; grid-template-columns: 1.35fr 1fr; gap: 14px; align-items: stretch; }
@media (max-width: 1100px) { .cdx-lower-grid { grid-template-columns: 1fr; } }

.cdx-spotlight {
  display: flex; align-items: center; gap: 12px;
  background: linear-gradient(135deg, color-mix(in srgb, var(--cdx-accent) 14%, var(--cdx-surface)), var(--cdx-surface));
  border: 1px solid var(--cdx-border); border-radius: 10px; padding: 12px 14px; margin: 4px 0 14px;
}
.cdx-spotlight-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.cdx-spotlight-line1 { font-size: 13.5px; font-weight: 700; color: var(--cdx-ink); display: flex; align-items: center; gap: 7px; }
.cdx-spotlight-line2 { font-size: 12px; color: var(--cdx-ink-muted); }
.cdx-spotlight-up { color: var(--cdx-status-good); font-weight: 700; }

.cdx-table-wrap, .cdx-scroll-table { overflow: auto; border-radius: 10px; }
.cdx-scroll-table { max-height: 340px; }
.cdx-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.cdx-table th {
  position: sticky; top: 0; background: var(--cdx-surface); z-index: 1;
  text-align: left; font-size: 11px; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--cdx-ink-faint); font-weight: 600; padding: 0 10px 10px; border-bottom: 1px solid var(--cdx-border);
}
.cdx-table th.num, .cdx-table td.num { text-align: right; }
.cdx-table td { padding: 11px 10px; border-bottom: 1px solid var(--cdx-border); vertical-align: middle; white-space: nowrap; }
.cdx-table tr:last-child td { border-bottom: none; }
.cdx-muted { color: var(--cdx-ink-soft); }
.cdx-empty-cell { text-align: center; color: var(--cdx-ink-faint); padding: 24px 10px; white-space: normal; }
.cdx-total-row td { font-weight: 750; border-top: 1px solid var(--cdx-border-strong); border-bottom: none; }

.cdx-agent-cell {
  appearance: none; border: none; background: none; cursor: pointer;
  display: inline-flex; align-items: center; gap: 10px;
  font: inherit; font-size: 13px; color: var(--cdx-ink); padding: 0;
}
.cdx-agent-cell:hover { color: var(--cdx-accent); }
.cdx-avatar {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: var(--cdx-page); flex: none;
}
.cdx-avatar--lg { width: 36px; height: 36px; font-size: 13px; }

.cdx-connect-track { width: 64px; height: 6px; border-radius: 4px; background: var(--cdx-surface-2); display: inline-block; overflow: hidden; vertical-align: middle; margin-right: 8px; }
.cdx-connect-fill { display: block; height: 100%; background: var(--cdx-accent); border-radius: 4px; }
.cdx-rank-lead { color: var(--cdx-status-good); font-weight: 700; }

.cdx-medal {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; font-size: 11.5px; font-weight: 800; color: #12172a;
}
.cdx-medal--gold { background: #e3b23c; }
.cdx-medal--silver { background: #c7ccd6; }
.cdx-medal--bronze { background: #cf8a4f; }
.cdx-rank-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; color: var(--cdx-ink-faint); font-size: 12px; font-weight: 600; }
.cdx-rank-move { font-size: 10.5px; font-weight: 700; margin-left: 4px; }
.cdx-rank-move--up { color: var(--cdx-status-good); }
.cdx-rank-move--down { color: var(--cdx-status-critical); }
.cdx-rank-move--flat { color: var(--cdx-ink-faint); }
.cdx-row--best td { background: color-mix(in srgb, var(--cdx-status-good) 8%, transparent); }
.cdx-row--clickable td { cursor: pointer; transition: background 0.1s ease; }
.cdx-row--clickable:hover td { background: color-mix(in srgb, var(--cdx-accent) 10%, transparent); }
/* Own-click cells (Total/Unique calls) sit inside a --clickable row but fire
   their own drilldown mode, so they need a hit target the full size of the
   cell -- not just the digit glyph -- plus a hover cue distinct from the
   row's, so it's obvious the whole box is tappable. */
.cdx-cell--click { cursor: pointer; position: relative; }
.cdx-cell--click:hover { background: color-mix(in srgb, var(--cdx-accent) 16%, transparent) !important; }
.cdx-cell--click::after { content: ''; position: absolute; inset: 0; }

.cdx-link { appearance: none; border: none; background: none; color: var(--cdx-accent); font: inherit; font-weight: 650; cursor: pointer; padding: 0; }
.cdx-link:disabled { opacity: 0.5; cursor: default; }

.cdx-trend-card { display: flex; flex-direction: column; }
.cdx-trend-legend { display: flex; gap: 16px; font-size: 12px; color: var(--cdx-ink-muted); margin-bottom: 6px; }
.cdx-trend-legend span { display: inline-flex; align-items: center; gap: 6px; }
.cdx-dot { width: 8px; height: 8px; border-radius: 50%; }
.cdx-chart-wrap { position: relative; flex: 1; min-height: 210px; }
.cdx-trend-svg { width: 100%; height: 100%; display: block; overflow: visible; }
.cdx-trend-axis-label { font-size: 10px; fill: var(--cdx-ink-faint); font-variant-numeric: tabular-nums; }
.cdx-trend-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 4px; }
.cdx-trend-head .cdx-card-title { margin-bottom: 0; }
.cdx-gridline { stroke: var(--cdx-grid-line); stroke-width: 1; }
.cdx-crosshair { stroke: var(--cdx-ink-faint); stroke-width: 1; stroke-dasharray: 3 3; }

.cdx-tooltip {
  position: absolute; pointer-events: none;
  background: var(--cdx-surface-2); border: 1px solid var(--cdx-border-strong);
  border-radius: 8px; padding: 8px 11px; font-size: 12px; color: var(--cdx-ink-soft);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  transform: translate(-50%, -112%); white-space: nowrap; z-index: 5;
}
.cdx-tooltip__date { color: var(--cdx-ink-faint); font-size: 10.5px; margin-bottom: 4px; }
.cdx-tooltip__row { display: flex; justify-content: space-between; gap: 14px; }
.cdx-tooltip__row .v { font-weight: 700; color: var(--cdx-ink); }

.cdx-tile-row--stat { margin-bottom: 4px; }
.cdx-stat-card { display: flex; flex-direction: column; gap: 5px; justify-content: center; }
.cdx-stat-label { margin: 0; font-size: 12px; color: var(--cdx-ink-faint); }
.cdx-stat-value { margin: 0; font-size: 22px; font-weight: 700; }
.cdx-stat-sub { margin: 0; font-size: 11.5px; color: var(--cdx-status-good); font-weight: 600; }
.cdx-stat-sub--muted { color: var(--cdx-ink-faint); }

.cdx-hour-legend { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--cdx-ink-muted); margin-bottom: 14px; flex-wrap: wrap; }
.cdx-metric-toggle { display: inline-flex; gap: 2px; background: var(--cdx-surface-2); border: 1px solid var(--cdx-border); border-radius: 7px; padding: 2px; }
.cdx-metric-btn {
  appearance: none; border: none; background: none;
  font: inherit; font-size: 11.5px; font-weight: 600; color: var(--cdx-ink-faint); cursor: pointer;
  padding: 4px 10px; border-radius: 5px;
}
.cdx-metric-btn.is-active { background: var(--cdx-surface); color: var(--cdx-ink); box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15); }
.cdx-ramp { width: 72px; height: 8px; border-radius: 4px; background: linear-gradient(90deg, color-mix(in srgb, var(--cdx-accent) 18%, var(--cdx-surface-2)), var(--cdx-accent)); }
.cdx-ramp-labels { display: flex; justify-content: space-between; width: 72px; font-size: 10.5px; color: var(--cdx-ink-faint); }
.cdx-best-flag { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; color: var(--cdx-status-good); font-weight: 600; }
.cdx-best-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--cdx-status-good); }

.cdx-hourchart-wrap { position: relative; height: 220px; }
.cdx-hourchart-svg { width: 100%; height: 100%; display: block; overflow: visible; }
.cdx-hourbar--best { stroke: var(--cdx-status-good); stroke-width: 2; }
.cdx-hour-axis-label { font-size: 10px; fill: var(--cdx-ink-faint); text-anchor: middle; }

.cdx-pill {
  display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 999px;
  font-size: 11px; font-weight: 700; background: rgba(124, 134, 166, 0.16); color: var(--cdx-ink-soft); flex: none;
}

.cdx-empty { margin: 0; font-size: 0.85rem; opacity: 0.65; }
.cdx-empty--error { color: #ff6b6b; opacity: 1; }

/* Drilldown modal -- same fixed-overlay popup as the design mockup, not an
   inline section. Teleported to <body>, but the overlay carries .cdx-root
   too so the component's CSS variables still resolve on it. */
.cdx-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(6, 8, 14, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 2400;
}
.cdx-modal-panel {
  width: min(680px, 100%);
  max-height: 82vh;
  background: var(--cdx-surface);
  border: 1px solid var(--cdx-border-strong);
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cdx-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--cdx-border);
  flex: none;
}
.cdx-modal-body { padding: 14px 18px 18px; overflow-y: auto; }
.cdx-drill__title { margin: 0.15rem 0 0; font-size: 16px; font-weight: 750; letter-spacing: -0.01em; }
.cdx-drill__summary { margin: 0.3rem 0 0; font-size: 12.5px; color: var(--cdx-ink-muted); }
.cdx-icon-btn {
  display: grid; place-items: center; width: 2rem; height: 2rem; border: 0; border-radius: 0.6rem;
  background: var(--cdx-surface-2); color: var(--cdx-ink-faint); cursor: pointer;
}
.cdx-icon-btn:hover { color: var(--cdx-ink); }
</style>
